import $                   from 'jquery';
import template            from './template.hbs';
import FormStep            from '../../classes/form-step';
import moment              from 'moment-timezone';
import calendar            from '../../templates/components/calendar.hbs';
// eslint-disable-next-line max-len
import AvailabilityService from '../../classes/availability/availability-service';

// all we care about here is the timezone, and the fallback
// is essentially just to prevent an error when the bundle gets
// loaded on a page without a preloaded place
const place = window.place_raw || { timezone: 'America/New_York' };

// set up the availability service
const availabilityService = new AvailabilityService({
  // number of total days in the calendar
  horizon: 90,
  // number of parallel AJAX requests
  concurrency: 3,
  // max number of days per AJAX request
  batchSize: 6,
  // number of days to preload
  preload: 30,
  // earliest possible slot – minutes from Date.now()
  buffer: location.search.indexOf('sameday=true') !== -1 ? 0 : 240,
  // timezone (for rendering)
  timezone: place.timezone,
  // place_id
  place_id: place._id
});

// this gets included in every bundle, but we only want to init on signup
if ( place._id && /^\/(signup|blackouts)/.test( location.pathname ) ) {
  availabilityService.start();
}

export default new FormStep({

  // root element
  $el: $('<form class="form-step step-schedule-install-fsl" />'),

  // handlebars template
  template,

  // current day (indexOf this.dayList)
  // if rendering multiple days at once, this is the *first* of the group
  dayIndex: 0,

  // array of AvailabilityDay objects
  dayList: place._id ? availabilityService.dates() : [],

  // are we in expanded view? ("more" button)
  expanded: false,

  // are we in a mobile layout
  mobile: window.innerWidth <= 600,

  // selected install slot { start: Date, finish: Date }
  selection: null,

  // current timezone
  tz: place.timezone,

  // have we sent availability data to acumen?
  availabilityTracked: false,

  events: {
    'submit-clicked': 'track'
  },

  // bind event andlers
  bindEvents() {
    this.$el.on( 'click', '.js-calendar-next', () => this.advanceCalendar() );
    this.$el.on( 'click', '.js-calendar-prev', () => this.reverseCalendar() );
    this.$el.on( 'click', '.js-more', () => this.onExpand() );
    this.$el.on( 'click', '.js-slot', ev => this.onSelect( ev ) );
    this.$el.on( 'click', '.js-next', ev => this.onNext( ev ) );
    this.$el.on( 'click', '.js-retry', ev => this.onRetry( ev ) );
    $( window ).on( 'resize', () => this.onResize() );

    return FormStep.prototype.bindEvents.call( this );
  },

  // # of days to render at a time
  daysToRender() {
    return this.mobile ? 1 : 3;
  },

  // advance the calendar
  advanceCalendar() {
    const index = this.dayIndex;
    const max   = this.dayList.length - 1 - ( this.daysToRender() - 1 );

    this.expanded = false;
    this.dayIndex = Math.min( index + this.daysToRender(), max );
    this.renderCalendar();
  },

  // reverse the calendar
  reverseCalendar() {
    this.expanded = false;
    this.dayIndex = Math.max( this.dayIndex - this.daysToRender(), 0 );
    this.renderCalendar();
  },

  // handle resize events
  onResize() {
    if ( window.innerWidth <= 600 && !this.mobile ) {
      this.mobile = true;
      this.renderCalendar();
    } else if ( window.innerWidth > 600 && this.mobile ) {
      this.mobile = false;
      this.renderCalendar();
    }
  },

  // handle expand clicks ("more" button)
  onExpand() {
    this.expanded = true;
    this.renderCalendar();
  },

  // handle date selection
  onSelect( ev ) {
    const el       = ev.target;
    const start    = new Date( el.dataset.start );
    const finish   = new Date( el.dataset.finish );
    const duration = parseInt( el.dataset.duration, 10 );

    this.$el.find('.js-dateError').text('').hide();
    this.selection = { start, finish, duration };
    this.dispatch({
      type: 'change',
      data: { install_date: moment( start ).tz( this.tz ).format() }
    });

    this.renderCalendar();
  },

  // handle next appointment click
  onNext( ev ) {
    const el    = ev.target;
    const index = parseInt( el.dataset.index, 10 );

    this.dayIndex = index;
    this.renderCalendar();
  },

  // handle slot error retry clicks
  onRetry() {
    this.getAvailabilityDays().forEach( day => day.reset() );
    this.renderCalendar();
  },

  // get an array of AvailabilityDay instaces we need to render
  getAvailabilityDays() {
    return this.dayList.slice(
      this.dayIndex,
      this.dayIndex + this.daysToRender()
    );
  },

  // get the next available day with open slots
  getNextAvailableDay() {
    const candidates = this.dayList.slice( this.dayIndex );

    for ( const day of candidates ) {
      if ( !day.isLoaded() ) {
        break;
      }

      if ( day.slots.length ) {
        return day;
      }
    }

    return null;
  },

  // get render context for days
  getDayRenderContext( days ) {
    return days.map( day => {
      return { day: day.prettyDay, date: day.prettyDate };
    });
  },

  // get render context for loading
  getLoadedRenderContext( days ) {
    return days.every( day => day.isLoaded() );
  },

  // get render context for slots
  getSlotRenderContext( days ) {
    const slotDays  = days.map( day => day.slots );
    const lengths   = slotDays.map( arr => arr.length );
    const maxLen    = Math.max.apply( Math, lengths );
    const maxSlots  = this.expanded ? maxLen : 4;
    const next      = this.getNextAvailableDay();
    const selection = this.selection && this.selection.start.getTime();

    // mobile & no slots? render a button to show the next available slot
    if ( maxLen === 0 && next ) {
      return [
        [
          {
            next: true,
            index: this.dayList.indexOf( next ),
            pretty: `${ next.prettyDay }, ${ next.prettyDate }`
          }
        ]
      ];
    }

    for ( const day of slotDays ) {
      const realLength = day.length;

      // chop it down
      day.length = maxSlots;

      // create render data for each day
      for ( let i = 0; i < maxSlots; ++i ) {
        // no slot here? render a spacer
        if ( !day[ i ] ) {
          day[ i ] = { spacer: true };
        } else {
          day[ i ] = {
            start: day[ i ].start,
            finish: day[ i ].finish,
            pretty: day[ i ].pretty,
            duration: day[ i ].duration,
            selected: selection && day[ i ].start.getTime() === selection
          };
        }
      }

      // let the template know it needs to render a "more" button
      if ( realLength > day.length ) {
        day.push({ more: true });
      }
    }

    return slotDays;
  },

  // get render context for selection
  getSelectionRenderContext() {
    if ( !this.selection ) {
      return null;
    }

    const start  = moment( this.selection.start ).tz( this.tz );
    const finish = moment( this.selection.finish ).tz( this.tz );

    return {
      date: start.format('MMMM Do'),
      start: start.format('h:mma'),
      finish: finish.format('h:mma')
    };
  },

  // get appointment duration render context
  getDurationRenderContext() {
    if ( !this.selection ) {
      return null;
    }

    const mins  = Math.min( this.selection.duration, 120 );
    const hours = Math.ceil( mins / 60 );

    return hours === 1 ? '1 hour' : `${ hours } hours`;
  },

  // isValid overrid
  // necessary because the base one only cares about <input/> elements
  // and we're not using one
  isValid( ...args ) {
    return this.selection && FormStep.prototype.isValid.call( this, ...args );
  },

  // render the calendar
  renderCalendar() {
    const container = this.$el[ 0 ].querySelector('.calendar-container');
    const days      = this.getAvailabilityDays();

    // stuff we still need
    const pending = days.filter( day => {
      return !day.isLoaded() && !day.isErrored();
    }).reverse();

    // are any of these loading errors?
    const error = days.some( day => day.isErrored() );

    // re-render once everything is loaded
    if ( pending.length ) {
      const proms = pending.map( day => availabilityService.load( day.key ) );
      const done  = this.renderCalendar.bind( this );

      Promise.all( proms ).then( done ).catch( done );
    }

    this.maybeTrackScheduleAvailability();

    container.innerHTML = calendar({
      error:      Boolean( error ),
      expanded:   Boolean( this.expanded ),
      hasNext:    this.dayIndex < this.dayList.length - 1 - ( days.length - 1 ),
      hasPrev:    this.dayIndex > 0,
      isLoaded:   this.getLoadedRenderContext( days ),
      dayHeaders: this.getDayRenderContext( days ),
      slotDays:   this.getSlotRenderContext( days ),
      selection:  this.getSelectionRenderContext(),
      duration:   this.getDurationRenderContext()
    }, {}, true );
  },

  // track schedule availability
  maybeTrackScheduleAvailability() {
    if ( this.availabilityTracked || !window.acumen ) {
      return;
    }

    const days   = this.dayList.slice( 0, 7 );
    const loaded = days.every( d => d.isLoaded() && !d.isErrored() );

    if ( !loaded ) {
      return;
    }

    this.availabilityTracked = true;

    window.acumen.track( 'web-track-availability', {
      day_0_count: days[ 0 ].slots.length,
      day_1_count: days[ 1 ].slots.length,
      day_2_count: days[ 2 ].slots.length,
      week_count: days.reduce( ( a, c ) => a + c.slots.length, 0 )
    });
  },

  // reset the calendar
  resetPicker() {
    this.dayIndex = 0;
    this.expanded = false;
    this.selection = null;
    this.dispatch({ type: 'change', data: { install_date: null } });
    availabilityService.reset();
    this.renderCalendar();
  },

  // show a date error
  showDateError( message ) {
    this.$el.find('.js-dateError').text( message ).show();
  },

  // render the full component
  render( state = {} ) {
    FormStep.prototype.render.call( this, state );
    this.renderCalendar();
  },

  // send data to acumen
  track( state ) {
    if ( window.acumen ) {
      window.acumen.track( 'web-submit-schedule_install', {
        install_time: state.install_date || null
      });
    }
  }

});
